/**
 * The one definition of the app's @font-face rules, shared by `build.ts`
 * (which writes it to dist/fonts.css) and the dev server (which generates it
 * per request) — the same shape as src/manifest.ts, and for the same reason:
 * two copies is how the manifest came to hardcode `scope: "/"`.
 *
 * Why this is NOT in src/index.css, where it belongs on the face of it: Bun's
 * CSS bundler inlines every woff2 it finds behind a `url()` as base64,
 * unconditionally — `loader: { ".woff2": "file" }` does not change it. That
 * turned the stylesheet into 303 KB of render-blocking bytes AND defeated the
 * `unicode-range` subsetting, since a subset embedded in the stylesheet is
 * downloaded whether or not a single character needs it. Served as files, the
 * browser fetches only the subsets a glyph actually requires.
 */

/** Ordered, and the order is asserted by src/fontCss.test.ts. */
export const FONT_FILES = [
  "inter-latin.woff2",
  "inter-latin-ext.woff2",
  "jetbrains-mono-latin.woff2",
  "jetbrains-mono-latin-ext.woff2",
] as const;

/** Google's subset ranges, kept verbatim so the browser can skip a whole file. */
const LATIN =
  "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, " +
  "U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, " +
  "U+2212, U+2215, U+FEFF, U+FFFD";
const LATIN_EXT =
  "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, " +
  "U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, " +
  "U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF";

interface Face {
  family: string;
  weight: string;
  file: (typeof FONT_FILES)[number];
  range: string;
}

const FACES: Face[] = [
  { family: "Inter", weight: "100 900", file: "inter-latin.woff2", range: LATIN },
  { family: "Inter", weight: "100 900", file: "inter-latin-ext.woff2", range: LATIN_EXT },
  { family: "JetBrains Mono", weight: "400 700", file: "jetbrains-mono-latin.woff2", range: LATIN },
  { family: "JetBrains Mono", weight: "400 700", file: "jetbrains-mono-latin-ext.woff2", range: LATIN_EXT },
];

/**
 * `base` always ends in "/" (see normalizeBase). Urls must be absolute from
 * it: this stylesheet lives at the deploy root but the document can be several
 * segments deep, and a relative url would resolve against the document and
 * 404 on every route deeper than the base.
 *
 * `font-display: swap` because the first paint is a table of figures the owner
 * wants to read now; blocking that on a font download is the wrong trade.
 */
export function buildFontCss(base: string): string {
  const header =
    "/* Generated by src/fontCss.ts. Do not edit: build.ts writes this file\n" +
    "   and the dev server serves the same output. SIL OFL 1.1 — see\n" +
    "   src/fonts/OFL-Inter.txt and src/fonts/OFL-JetBrainsMono.txt. */\n";

  return (
    header +
    FACES.map(
      (face) => `
@font-face {
  font-family: "${face.family}";
  font-style: normal;
  font-weight: ${face.weight};
  font-display: swap;
  src: url("${base}fonts/${face.file}") format("woff2");
  unicode-range: ${face.range};
}`,
    ).join("\n") +
    "\n"
  );
}
